import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Menu, X, LogIn, Package } from 'lucide-react';
import Login from '@/components/auth/Login';
import { cn } from '@/utils/merge';
import { NAV, navHref } from './nav';
import { useInterest } from './interest';
import { Action, Container } from './ui';

const subscribeScroll = cb => {
	window.addEventListener('scroll', cb, { passive: true });
	return () => window.removeEventListener('scroll', cb);
};
const isScrolled = () => window.scrollY > 8;

const NavLink = ({ item, onHome, className, onClick }) =>
	item.to ? (
		<Link to={item.to} className={className} onClick={onClick}>
			{item.label}
		</Link>
	) : (
		<a href={navHref(item, onHome)} className={className} onClick={onClick}>
			{item.label}
		</a>
	);

const Logo = () => (
	<img
		src='/images/logo_rect.png'
		alt='DeliveryMan'
		width={1419}
		height={319}
		className='h-8 w-auto sm:h-9'
		fetchPriority='high'
	/>
);

export const PublicHeader = () => {
	const location = useLocation();
	const onHome = location.pathname === '/';
	const onOrderPage = location.pathname === '/paraggelia';
	const { openInterest } = useInterest();
	const [menuOpen, setMenuOpen] = useState(false);
	const scrolled = useSyncExternalStore(subscribeScroll, isScrolled, () => false);
	const loginRef = useRef(null);

	// Mobile menu: lock the page behind it and close on Escape.
	useEffect(() => {
		if (!menuOpen) return;
		const prev = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		const onKey = e => e.key === 'Escape' && setMenuOpen(false);
		window.addEventListener('keydown', onKey);
		return () => {
			document.body.style.overflow = prev;
			window.removeEventListener('keydown', onKey);
		};
	}, [menuOpen]);

	const openLogin = () => {
		setMenuOpen(false);
		loginRef.current?.showModal();
	};
	const closeLogin = () => loginRef.current?.close();

	const bookDemo = () => {
		setMenuOpen(false);
		if (onHome || location.pathname === '/perioxes') openInterest('fleet');
	};

	return (
		<header
			className={cn(
				'fixed inset-x-0 top-0 z-50 border-b transition-[background-color,box-shadow,border-color] duration-300',
				scrolled || menuOpen
					? 'border-(--dm-line) bg-white/95 shadow-[0_4px_20px_rgba(16,34,53,0.06)] backdrop-blur-md'
					: 'border-transparent bg-(--dm-bg)/80 backdrop-blur-sm',
			)}
		>
			<div className='mx-auto flex h-[72px] w-full max-w-[1320px] items-center gap-4 px-4 sm:px-6 lg:px-8'>
				<Link to='/' aria-label='DeliveryMan — αρχική' className='shrink-0'>
					<Logo />
				</Link>

				<nav aria-label='Κύριο μενού' className='ml-4 hidden flex-1 xl:block'>
					<ul className='flex items-center justify-center'>
						{NAV.map(item => (
							<li key={item.label}>
								<NavLink
									item={item}
									onHome={onHome}
									className={cn(
										'rounded-lg px-2 py-2 text-[14px] font-medium whitespace-nowrap text-(--dm-muted) transition-colors hover:bg-(--dm-blue-soft) hover:text-(--dm-navy)',
										item.to === location.pathname && 'text-(--dm-navy)',
									)}
								/>
							</li>
						))}
					</ul>
				</nav>

				<div className='ml-auto flex items-center gap-2'>
					<button
						type='button'
						onClick={openLogin}
						aria-label='Σύνδεση'
						className='hidden min-h-11 items-center gap-1.5 rounded-xl px-3 text-[14px] font-semibold whitespace-nowrap text-(--dm-navy) transition-colors hover:bg-(--dm-blue-soft) sm:inline-flex'
					>
						<LogIn size={16} aria-hidden='true' />
						Σύνδεση
					</button>
					<Action
						href={onHome || location.pathname === '/perioxes' ? '#endiaferon' : '/#endiaferon'}
						onClick={e => {
							if (onHome || location.pathname === '/perioxes') {
								e.preventDefault();
								bookDemo();
							}
						}}
						className='min-h-11 px-4 text-[14px] whitespace-nowrap sm:px-5'
					>
						Κλείσε Demo
					</Action>
					<button
						type='button'
						className='inline-flex h-11 w-11 items-center justify-center rounded-xl text-(--dm-navy) hover:bg-(--dm-blue-soft) xl:hidden'
						aria-expanded={menuOpen}
						aria-controls='dm-mobile-menu'
						aria-label={menuOpen ? 'Κλείσιμο μενού' : 'Άνοιγμα μενού'}
						onClick={() => setMenuOpen(o => !o)}
					>
						{menuOpen ? <X size={24} /> : <Menu size={24} />}
					</button>
				</div>
			</div>

			{/* Private customers: the order page, as a strip under the bar on every
			    screen — the bar itself has no room left beside the full menu. */}
			{!menuOpen && !onOrderPage && (
				<Link
					to='/paraggelia'
					className='flex h-[34px] items-center justify-center gap-1.5 border-t border-(--dm-line) bg-(--dm-blue-soft) text-[14px] font-semibold text-(--dm-navy) transition-colors hover:text-(--dm-blue)'
				>
					<Package size={16} className='text-(--dm-blue)' aria-hidden='true' />
					Ιδιώτης; Στείλτε παραγγελία
					<ArrowRight size={16} aria-hidden='true' />
				</Link>
			)}

			{menuOpen && (
				<nav
					id='dm-mobile-menu'
					aria-label='Μενού'
					className='h-[calc(100dvh-72px)] overflow-y-auto border-t border-(--dm-line) bg-white xl:hidden'
				>
					<Container className='flex flex-col gap-1 py-4'>
						{NAV.map(item => (
							<NavLink
								key={item.label}
								item={item}
								onHome={onHome}
								onClick={() => setMenuOpen(false)}
								className='rounded-xl px-3 py-3.5 text-lg font-semibold text-(--dm-text) hover:bg-(--dm-blue-soft)'
							/>
						))}
						<div className='mt-4 flex flex-col gap-3 border-t border-(--dm-line) pt-5'>
							<Link
								to='/paraggelia'
								onClick={() => setMenuOpen(false)}
								className='inline-flex min-h-12 items-center justify-center gap-2 rounded-xl border border-(--dm-line) bg-white px-6 text-[15px] font-semibold text-(--dm-navy) shadow-sm'
							>
								<Package size={18} aria-hidden='true' />
								Στείλτε παραγγελία
							</Link>
							<Action variant='secondary' onClick={openLogin}>
								<LogIn size={18} aria-hidden='true' />
								Σύνδεση
							</Action>
						</div>
					</Container>
				</nav>
			)}

			{/* The app's own login form, unchanged — only shown in a dialog here. */}
			<dialog
				ref={loginRef}
				className='dm-login-dialog'
				aria-label='Σύνδεση'
				onClick={e => e.target === loginRef.current && closeLogin()}
			>
				<div className='relative w-[340px] max-w-full rounded-2xl bg-white p-6 pt-12 shadow-(--dm-shadow-lg)'>
					<p className='absolute top-5 left-6 text-lg font-bold text-(--dm-text)'>Σύνδεση</p>
					<button
						type='button'
						onClick={closeLogin}
						aria-label='Κλείσιμο'
						className='absolute top-3 right-3 inline-flex h-10 w-10 items-center justify-center rounded-lg text-(--dm-muted) hover:bg-(--dm-blue-soft)'
					>
						<X size={20} />
					</button>
					<Login onLoginCb={closeLogin} onLogoutCb={closeLogin} />
				</div>
			</dialog>
		</header>
	);
};
